import React, { useState } from 'react';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { Checkbox, Collapse, Modal, Tooltip } from '../../../components';
import { RuleBody } from './components/rule-body';
import { RuleHeader } from './components/rule-header';
import { RuleFooter } from './components/rule-footer';

import { Attribute, Condition, getEmptyCondition, toggleAttrOptions, findAttribute } from './rule-utils';
import { useAttrOptionsState } from '../hooks';
import type { NumericTranslations } from '../numeric';
import type { FreeTextTranslations } from '../free-text';
import type { BooleanTranslations } from '../boolean/boolean-condition';
import { ArrayTranslations } from '../array/array-condition';

import './rule.scss';
import styles from './rule.module.scss';
import { DEFAULT_PANEL_KEY } from '../../../components/collapse/collapse';

export interface ConditionsTranslations {
	numeric: NumericTranslations;
	array: ArrayTranslations;
	freeText: FreeTextTranslations;
	addAttribute: string;
	removeTooltipText?: string;

	getBooleanTranslations(attribute_name: string): BooleanTranslations;
}

export interface RuleTranslations extends ConditionsTranslations {
	deleteTitle: string;
	deleteBody: string;
	cancel: string;
	delete: string;
	inactive?: string;
	hardConstraint?: string;
	hardConstraintTooltip?: string;
}

export interface CommonRuleProps {
	divider?: React.ReactNode;
	parentContainerClass?: string;
	contextSelector?: string;
	disabled?: boolean;
	maxAttributePathItems?: number;
}

export interface RuleProps extends CommonRuleProps {
	name: string;
	conditions: Condition[];
	translations: RuleTranslations;

	onRemove?(): void;

	onNameChange?(name: string): void;

	setDirty(conditions: Condition[]): void;

	setHardConstraint?(checked: boolean): void;

	attributes: Attribute[];
	hardConstraint?: boolean;
	isOpen?: boolean;
	isEnabled?: boolean;

	toggleOpen?(isOpen: boolean): void;

	toggleEnabled?(isEnabled: boolean): void;
}

const Rule = ({
	conditions,
	name,
	onRemove,
	translations,
	attributes,
	hardConstraint = false,
	setDirty,
	isOpen = true,
	isEnabled,
	toggleOpen,
	toggleEnabled,
	onNameChange,
	setHardConstraint = null,
	divider,
	parentContainerClass,
	contextSelector,
	maxAttributePathItems,
	disabled = false
}: RuleProps) => {
	const [attrOptions, setAttrOptions] = useAttrOptionsState(conditions, attributes);
	const [isVisible, setIsVisible] = useState(false);

	const handleChange = (condition, value) => {
		const mappedConditions = conditions.map(cond =>
			cond.id === condition.id
				? {
						...condition,
						value
				  }
				: cond
		);
		setDirty(mappedConditions);
	};

	const removeCondition = (condition: Condition) => {
		setDirty(conditions.filter(cond => cond.id !== condition.id));
		const attribute = findAttribute(attrOptions, condition.path);
		if (!attribute) return;
		setAttrOptions(toggleAttrOptions(attr => attr.path === attribute.path, { disabled: false }, attrOptions));
	};

	const addCondition = (key: string) => {
		const attribute = findAttribute(attrOptions, key);
		if (!attribute) return;
		const { type, options, path, display_path } = attribute;
		if (!type || !display_path) return;
		const newCondition = getEmptyCondition({
			name: display_path,
			index: conditions.length,
			path,
			type,
			options,
			onChange: handleChange,
			onRemove: removeCondition
		});
		setDirty([...conditions, newCondition]);
	};

	const removeRule = () => {
		closeModal();
		onRemove();
	};

	const closeModal = () => setIsVisible(false);

	const openModal = event => {
		event.stopPropagation();
		setIsVisible(true);
	};

	const toggleSwitch = (checked: boolean, e) => {
		e.stopPropagation();
		toggleEnabled(checked);
	};

	const changeHardConstraint = () => {
		setHardConstraint(!hardConstraint);
	};

	const isCollapsable = !!toggleOpen;
	const headerClasses = classNames('header', {
		collapsable: isCollapsable
	});

	const header = (
		<RuleHeader
			name={name}
			className={headerClasses}
			onNameChange={onNameChange}
			onRemove={onRemove ? openModal : undefined}
			switchChecked={isEnabled}
			toggleSwitch={toggleEnabled ? toggleSwitch : undefined}
			disabled={disabled}
			inactiveText={translations.inactive}
		/>
	);

	return (
		<React.Fragment>
			<Collapse
				className="rule-container"
				defaultActiveKey={isOpen ? DEFAULT_PANEL_KEY : ''}
				onChange={toggleOpen}
			>
				<Collapse.Panel key={DEFAULT_PANEL_KEY} header={header} showArrow={isCollapsable}>
					{setHardConstraint !== null && (
						<div className={styles['hard-constraint']}>
							<Checkbox onClick={changeHardConstraint} checked={hardConstraint}>
								{translations.hardConstraint}
							</Checkbox>
							<Tooltip placement="topLeft" title={translations.hardConstraintTooltip}>
								<span className={styles['info-button']}>
									<BringgIcon iconName={BringgFontIcons.Info} />
								</span>
							</Tooltip>
						</div>
					)}
					<RuleBody
						maxAttributePathItems={maxAttributePathItems}
						conditions={conditions}
						translations={translations}
						divider={divider}
						parentContainerClass={parentContainerClass}
						contextSelector={contextSelector}
						handleChange={handleChange}
						removeCondition={removeCondition}
						disabled={disabled}
					/>
					<RuleFooter
						disabled={disabled}
						attributes={attrOptions}
						addCondition={addCondition}
						translations={translations}
						contextSelector={contextSelector}
					/>
				</Collapse.Panel>
			</Collapse>
			<Modal
				title={translations.deleteTitle}
				visible={isVisible}
				onOk={removeRule}
				onCancel={closeModal}
				okText={translations.delete}
				cancelText={translations.cancel}
				okType={'danger'}
			>
				{translations.deleteBody}
			</Modal>
		</React.Fragment>
	);
};

export default Rule;
